import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { DropdownOption, GlobalDropdownField } from '../../components/GlobalComponents/GlobalDropdownField';
import { GlobalTimeField } from '../../components/GlobalComponents/GlobalTimeField';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
import { GlobalInputField } from '../../components/GlobalComponents/GlobalInputField';

interface FeesSlab {
  fees_slab_id: string;
  base_fee: number;
  minimum_fee: number;
  maximum_fee: number;
  status: string;
  created_at?: string;
}

interface Course {
  course_id: string;
  name: string;
  title: string;
  fees_slab?: FeesSlab | null;
}

interface Batch {
  batch_id: string;
  institute_id: string;
  branch_id: string;
  course_id: string;
  batch_name: string;
  batch_time: string;
  start_month: string;
  start_year: number;
  end_month: string;
  end_year: number;
  is_active: boolean;
  created_at: string | null;
}

interface AddBatchModalProps {
  visible: boolean;
  instituteId: string;
  branchId: string;
  courses: Course[];
  editingBatch?: Batch | null;
  onHide: () => void;
  onSuccess?: () => void;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// ── Design tokens — navy / gold ───────────────────────────────────────────────
const C = {
  navy:       '#0e225d',
  navyMid:    '#1a3370',
  navyDark:   '#0a1a3d',
  gold:       '#fbb21f',
  goldLight:  '#ffc93c',
  goldDim:    'rgba(251,178,31,0.18)',
  goldBorder: 'rgba(251,178,31,0.35)',
  white:      '#ffffff',
  border:     '#E5E7EB',
  bg:         '#F5F7FA',
  muted:      '#9ca3af',
  sub:        '#6b7280',
  body:       '#1f2937',
};

const AddBatchModal: React.FC<AddBatchModalProps> = ({
  visible, instituteId, branchId, courses, editingBatch, onHide, onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState<string | null>(null);
  const [feesSlab, setFeesSlab] = useState<FeesSlab | null>(null);

  const [formData, setFormData] = useState({
    course_id:      '',
    batch_name:     '',
    start_time:     '',
    end_time:       '',
    start_month:    '',
    start_year:     new Date().getFullYear(),
    end_month:      '',
    end_year:       new Date().getFullYear() + 1,
    is_active:      true,
    additional_fee: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // ── Sync fees slab from selected course ──────────────────────────────────────
  useEffect(() => {
    if (!formData.course_id) { setFeesSlab(null); setError(null); return; }
    const selected = courses.find(c => c.course_id === formData.course_id);
    if (selected?.fees_slab) { setFeesSlab(selected.fees_slab); setError(null); }
    else { setFeesSlab(null); setError('No fees slab found for the selected course. Please contact the administrator.'); }
  }, [formData.course_id, courses]);

  // ── Populate when editing ────────────────────────────────────────────────────
  useEffect(() => {
    if (editingBatch) {
      const [startTime, endTime] = editingBatch.batch_time.split(' - ').map(t => t.trim());
      setFormData({
        course_id: editingBatch.course_id, batch_name: editingBatch.batch_name,
        start_time: startTime || '', end_time: endTime || '',
        start_month: editingBatch.start_month, start_year: editingBatch.start_year,
        end_month: editingBatch.end_month, end_year: editingBatch.end_year,
        is_active: editingBatch.is_active, additional_fee: ''
      });
    } else {
      setFormData({
        course_id: '', batch_name: '', start_time: '', end_time: '',
        start_month: '', start_year: new Date().getFullYear(),
        end_month: '', end_year: new Date().getFullYear() + 1,
        is_active: true, additional_fee: ''
      });
      setFeesSlab(null); setError(null);
    }
  }, [editingBatch, visible]);

  // ── Generate batch name ──────────────────────────────────────────────────────
  const canGenerateName = !!(formData.course_id && formData.start_month && formData.start_year && formData.end_year);

  const handleGenerateBatchName = () => {
    if (!canGenerateName) return;
    const selectedCourse = courses.find(c => c.course_id === formData.course_id);
    if (!selectedCourse) return;
    const coursePart = `${selectedCourse.name} - ${selectedCourse.title}`.toUpperCase();
    const monthPart  = formData.start_month.toUpperCase().slice(0, 3);
    const generated  = `${coursePart}-${monthPart}-${formData.start_year}-${formData.end_year}`;
    setFormData(prev => ({ ...prev, batch_name: generated }));
    if (errors.batch_name) setErrors(prev => ({ ...prev, batch_name: '' }));
  };

  // ── Options ──────────────────────────────────────────────────────────────────
  const courseOptions: DropdownOption[] = courses.map(c => ({ label: `${c.name} - ${c.title}`, value: c.course_id }));
  const monthOptions:  DropdownOption[] = MONTHS.map(m => ({ label: m, value: m }));
  const currentYear = new Date().getFullYear();
  const yearOptions: DropdownOption[] = Array.from({ length: 7 }, (_, i) => currentYear - 1 + i).map(y => ({ label: y.toString(), value: y }));
  const statusOptions: DropdownOption[] = [{ label: 'Active', value: true }, { label: 'Inactive', value: false }];

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    if (field === 'additional_fee' && feesSlab) {
      const v = parseFloat(value);
      if (value && !isNaN(v) && v > feesSlab.maximum_fee) {
        setErrors(prev => ({ ...prev, additional_fee: `Cannot exceed ₹${feesSlab.maximum_fee.toLocaleString('en-IN')}` }));
      }
    }
  };

  const parseTimeToMinutes = (t: string) => {
    if (!t) return 0;
    const [time, meridiem] = t.split(' ');
    const [h, m] = time.split(':').map(Number);
    let adj = h;
    if (meridiem?.toUpperCase() === 'PM' && h !== 12) adj = h + 12;
    else if (meridiem?.toUpperCase() === 'AM' && h === 12) adj = 0;
    return adj * 60 + m;
  };

  const validateForm = () => {
    const e: Record<string, string> = {};
    if (!formData.course_id)          e.course_id   = 'Course is required';
    if (!formData.batch_name.trim())  e.batch_name  = 'Batch name is required';
    if (!formData.start_time.trim())  e.start_time  = 'Start time is required';
    if (!formData.end_time.trim())    e.end_time    = 'End time is required';
    if (!formData.start_month)        e.start_month = 'Start month is required';
    if (!formData.start_year)         e.start_year  = 'Start year is required';
    if (!formData.end_month)          e.end_month   = 'End month is required';
    if (!formData.end_year)           e.end_year    = 'End year is required';
    if (formData.start_time && formData.end_time) {
      if (parseTimeToMinutes(formData.end_time) <= parseTimeToMinutes(formData.start_time))
        e.end_time = 'End time must be after start time';
    }
    if (formData.start_year && formData.end_year && formData.start_month && formData.end_month) {
      const s = new Date(formData.start_year, MONTHS.indexOf(formData.start_month));
      const en = new Date(formData.end_year, MONTHS.indexOf(formData.end_month));
      if (en <= s) { e.end_month = 'End date must be after start date'; e.end_year = 'End date must be after start date'; }
    }
    if (formData.additional_fee && feesSlab) {
      const v = parseFloat(formData.additional_fee);
      if (isNaN(v) || v < 0) e.additional_fee = 'Must be a valid positive number';
      else if (v > feesSlab.maximum_fee) e.additional_fee = `Cannot exceed ₹${feesSlab.maximum_fee.toLocaleString('en-IN')}`;
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) { setError('Please fill all required fields correctly'); return; }
    setLoading(true); setError(null);
    try {
      const payload = {
        institute_id: instituteId, branch_id: branchId,
        course_id: formData.course_id, batch_name: formData.batch_name.trim(),
        batch_time: `${formData.start_time.trim()} - ${formData.end_time.trim()}`,
        start_month: formData.start_month, start_year: formData.start_year,
        end_month: formData.end_month, end_year: formData.end_year,
        is_active: formData.is_active, base_fee: feesSlab?.base_fee || 0,
        additional_fee: formData.additional_fee ? parseFloat(formData.additional_fee) : 0
      };
      const response = editingBatch
        ? await apiClient.put(`/v1/batches/${editingBatch.batch_id}`, payload)
        : await apiClient.post('/v1/batches', payload);
      if (response.data.success) { handleClose(); onSuccess?.(); }
      else setError(response.data.message || `Failed to ${editingBatch ? 'update' : 'create'} batch`);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || `Failed to ${editingBatch ? 'update' : 'create'} batch`);
    } finally { setLoading(false); }
  };

  const handleClose = () => {
    setFormData({ course_id: '', batch_name: '', start_time: '', end_time: '', start_month: '', start_year: new Date().getFullYear(), end_month: '', end_year: new Date().getFullYear() + 1, is_active: true, additional_fee: '' });
    setErrors({}); setError(null); setFeesSlab(null); onHide();
  };

  const calculateTotalFee = () => {
    if (!feesSlab) return 0;
    const add = parseFloat(formData.additional_fee);
    return feesSlab.base_fee + (isNaN(add) ? 0 : add);
  };

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      style={{ width: '90vw', maxWidth: '900px' }}
      dismissableMask
      draggable={false}
      pt={{
        root: { style: { borderRadius: '20px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(14,34,93,0.25)' } },
        header: { style: { display: 'none' } },        // hide default PrimeReact header
        content: { style: { padding: '24px' } }
      }}
    >
      <div>
        {/* ── Modal Header — navy gradient ─────────────────────────────── */}
        <div style={{
          background: `linear-gradient(180deg, ${C.navy} 0%, ${C.navyMid} 50%, ${C.navyDark} 100%)`,
          borderRadius: '16px', padding: '28px', marginBottom: '24px',
          color: C.white, position: 'relative', overflow: 'hidden'
        }}>
          {/* Decorative orbs */}
          <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '180px', height: '180px', background: C.goldDim, borderRadius: '50%', filter: 'blur(40px)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', bottom: '-40px', left: '20%', width: '120px', height: '120px', background: 'rgba(251,178,31,0.05)', borderRadius: '50%', filter: 'blur(30px)', pointerEvents: 'none' }} />

          <div style={{ position: 'relative', zIndex: 1 }}>
            {/* Badge pill */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '4px 12px',
              background: C.goldDim, border: `1px solid ${C.goldBorder}`,
              borderRadius: '20px', marginBottom: '12px',
              fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.8px',
              textTransform: 'uppercase', color: C.gold
            }}>
              <i className="pi pi-th-large" style={{ fontSize: '0.7rem' }} />
              Batch Management
            </div>

            <h2 style={{ margin: '0 0 6px 0', fontSize: '1.5rem', fontWeight: '700', color: C.white, letterSpacing: '-0.5px' }}>
              {editingBatch ? 'Edit Batch' : 'Add New Batch'}
            </h2>
            <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: '0.85rem' }}>
              {editingBatch ? 'Update batch information below' : 'Create a new batch for a course'}
            </p>
          </div>
        </div>

        {/* ── Error banner ─────────────────────────────────────────────── */}
        {error && (
          <Message severity="error" text={error} style={{ marginBottom: '20px', fontSize: '0.9rem', fontWeight: '500' }} />
        )}

        {/* ── Form body ─────────────────────────────────────────────────── */}
        <div style={{
          background: C.white, borderRadius: '16px', padding: '24px',
          marginBottom: '20px', border: `1px solid ${C.border}`,
          boxShadow: '0 2px 8px rgba(14,34,93,0.06)'
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

            {/* Course */}
            <GlobalDropdownField
              label="Course"
              required
              value={formData.course_id}
              onChange={(val) => handleInputChange('course_id', val)}
              options={courseOptions}
              placeholder="Select a course"
              icon="pi pi-book"
              error={errors.course_id}
              disabled={!!editingBatch}
            />

            {/* ── Fees Information ─────────────────────────────────────── */}
            {feesSlab && (
              <SectionBox title="Fees Information" icon="pi-wallet">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
                  {/* Base fee */}
                  <div style={{ background: C.bg, padding: '14px', borderRadius: '10px', border: `1px solid ${C.border}` }}>
                    <p style={labelStyle}>Institute Proposed Fee</p>
                    <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800', color: C.navy }}>
                      ₹{feesSlab.base_fee.toLocaleString('en-IN')}
                    </p>
                  </div>
                  {/* Max additional */}
                  <div style={{ background: 'rgba(251,178,31,0.07)', padding: '14px', borderRadius: '10px', border: `1px solid ${C.goldBorder}` }}>
                    <p style={labelStyle}>Max. Additional Allowed</p>
                    <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800', color: '#92580a' }}>
                      ₹{feesSlab.maximum_fee.toLocaleString('en-IN')}
                    </p>
                  </div>
                </div>

                <GlobalInputField
                  label="Additional Fee (Optional)"
                  value={formData.additional_fee}
                  onChange={(e) => handleInputChange('additional_fee', e.target.value)}
                  placeholder="0.00"
                  icon="pi pi-plus-circle"
                  error={errors.additional_fee}
                  type="number"
                />

                {/* Total preview */}
                {(formData.additional_fee || formData.additional_fee === '0') && !errors.additional_fee && (
                  <div style={{
                    marginTop: '12px', padding: '12px 16px',
                    background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`,
                    borderRadius: '10px',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                  }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: '600', color: 'rgba(255,255,255,0.7)' }}>Total Batch Fee</span>
                    <span style={{ fontSize: '1.2rem', fontWeight: '800', color: C.gold }}>
                      ₹{calculateTotalFee().toLocaleString('en-IN')}
                    </span>
                  </div>
                )}

                <p style={{ margin: '10px 0 0 0', fontSize: '0.72rem', color: C.muted, display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <i className="pi pi-info-circle" style={{ fontSize: '0.68rem' }} />
                  Additional fee cannot exceed ₹{feesSlab.maximum_fee.toLocaleString('en-IN')}
                </p>
              </SectionBox>
            )}

            {/* ── Start Date ────────────────────────────────────────────── */}
            <SectionBox title="Start Date" icon="pi-calendar-plus">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <GlobalDropdownField
                  label="Start Month" required value={formData.start_month}
                  onChange={(val) => handleInputChange('start_month', val)}
                  options={monthOptions} placeholder="Select month"
                  icon="pi pi-calendar" error={errors.start_month}
                />
                <GlobalDropdownField
                  label="Start Year" required value={formData.start_year}
                  onChange={(val) => handleInputChange('start_year', val)}
                  options={yearOptions} placeholder="Select year"
                  icon="pi pi-calendar" error={errors.start_year}
                />
              </div>
            </SectionBox>

            {/* ── End Date ──────────────────────────────────────────────── */}
            <SectionBox title="End Date" icon="pi-calendar-minus">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <GlobalDropdownField
                  label="End Month" required value={formData.end_month}
                  onChange={(val) => handleInputChange('end_month', val)}
                  options={monthOptions} placeholder="Select month"
                  icon="pi pi-calendar" error={errors.end_month}
                />
                <GlobalDropdownField
                  label="End Year" required value={formData.end_year}
                  onChange={(val) => handleInputChange('end_year', val)}
                  options={yearOptions} placeholder="Select year"
                  icon="pi pi-calendar" error={errors.end_year}
                />
              </div>
            </SectionBox>

            {/* ── Batch Name + Generate ─────────────────────────────────── */}
            <div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <GlobalInputField
                    label="Batch Name" required value={formData.batch_name}
                    onChange={(e) => handleInputChange('batch_name', e.target.value)}
                    placeholder="e.g., ADCA-JAN-2025-2026"
                    icon="pi pi-users" error={errors.batch_name}
                  />
                </div>

                {/* Generate button — gold when active, muted when not */}
                <button
                  type="button"
                  onClick={handleGenerateBatchName}
                  disabled={!canGenerateName}
                  title={canGenerateName ? 'Generate batch name' : 'Select Course, Start Month, Start Year and End Year first'}
                  style={{
                    height: '44px', padding: '0 18px',
                    background: canGenerateName ? C.gold : C.bg,
                    border: canGenerateName ? 'none' : `1px solid ${C.border}`,
                    borderRadius: '10px',
                    color: canGenerateName ? C.navy : C.muted,
                    fontSize: '0.82rem', fontWeight: '700',
                    cursor: canGenerateName ? 'pointer' : 'not-allowed',
                    display: 'flex', alignItems: 'center', gap: '6px',
                    whiteSpace: 'nowrap', transition: 'all 0.2s ease', flexShrink: 0,
                    boxShadow: canGenerateName ? '0 2px 8px rgba(251,178,31,0.35)' : 'none',
                    marginBottom: errors.batch_name ? '22px' : '0'
                  }}
                  onMouseEnter={e => { if (canGenerateName) { e.currentTarget.style.background = C.goldLight; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(251,178,31,0.5)'; } }}
                  onMouseLeave={e => { if (canGenerateName) { e.currentTarget.style.background = C.gold; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(251,178,31,0.35)'; } }}
                >
                  <i className="pi pi-bolt" style={{ fontSize: '0.85rem' }} />
                  Generate
                </button>
              </div>

              {!canGenerateName && (
                <p style={{ margin: '6px 0 0 0', fontSize: '0.72rem', color: C.muted, display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <i className="pi pi-info-circle" style={{ fontSize: '0.68rem' }} />
                  Select Course, Start Month, Start Year and End Year to auto-generate a name
                </p>
              )}
            </div>

            {/* ── Batch Timings ─────────────────────────────────────────── */}
            <SectionBox title="Batch Timings" icon="pi-clock">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <GlobalTimeField
                  label="Start Time" required value={formData.start_time}
                  onChange={(val) => handleInputChange('start_time', val)}
                  placeholder="e.g., 10:00 AM" icon="pi pi-clock"
                  error={errors.start_time} hourFormat="12"
                />
                <GlobalTimeField
                  label="End Time" required value={formData.end_time}
                  onChange={(val) => handleInputChange('end_time', val)}
                  placeholder="e.g., 12:00 PM" icon="pi pi-clock"
                  error={errors.end_time} hourFormat="12"
                />
              </div>

              {/* Schedule preview — navy pill */}
              {formData.start_time && formData.end_time && !errors.end_time && (
                <div style={{
                  marginTop: '12px', padding: '10px 16px',
                  background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navyMid} 100%)`,
                  borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                }}>
                  <i className="pi pi-clock" style={{ fontSize: '0.85rem', color: C.gold }} />
                  <span style={{ fontSize: '0.85rem', fontWeight: '700', color: C.white }}>
                    {formData.start_time} – {formData.end_time}
                  </span>
                </div>
              )}
            </SectionBox>

            {/* Status */}
            <GlobalDropdownField
              label="Status" required value={formData.is_active}
              onChange={(val) => handleInputChange('is_active', val)}
              options={statusOptions} placeholder="Select status"
              icon="pi pi-check-circle"
            />
          </div>
        </div>

        {/* ── Actions ───────────────────────────────────────────────────── */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <GlobalButton
            label="Cancel" variant="outline" icon="pi pi-times"
            onClick={handleClose} disabled={loading}
          />
          <GlobalButton
            label={loading ? (editingBatch ? 'Updating...' : 'Creating...') : (editingBatch ? 'Update Batch' : 'Create Batch')}
            variant="primary"
            icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
            onClick={handleSubmit}
            disabled={loading || !feesSlab}
            loading={loading}
          />
        </div>
      </div>
    </Dialog>
  );
};

// ── Shared label style ────────────────────────────────────────────────────────
const labelStyle: React.CSSProperties = {
  margin: '0 0 5px 0', fontSize: '0.68rem', color: '#9ca3af',
  fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.6px'
};

// ── SectionBox — navy accent bar + gold icon, matches card sections ───────────
const SectionBox: React.FC<{ title: string; icon: string; children: React.ReactNode }> = ({ title, icon, children }) => (
  <div style={{
    background: '#F5F7FA', borderRadius: '14px', padding: '18px',
    border: '1px solid #E5E7EB'
  }}>
    {/* Section header — matches the Batches.tsx "Search Batches" bar style */}
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
      <div style={{
        width: '32px', height: '32px',
        background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
        borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
      }}>
        <i className={`pi ${icon}`} style={{ color: '#fbb21f', fontSize: '0.85rem' }} />
      </div>
      <span style={{ fontSize: '0.88rem', fontWeight: '700', color: '#0e225d', letterSpacing: '0.2px' }}>
        {title}
      </span>
    </div>
    {children}
  </div>
);

export default AddBatchModal;



// import React, { useState, useEffect } from 'react';
// import { Dialog } from 'primereact/dialog';
// import { Message } from 'primereact/message';
// import { DropdownOption, GlobalDropdownField } from '../../components/GlobalComponents/GlobalDropdownField';
// import { GlobalTimeField } from '../../components/GlobalComponents/GlobalTimeField';
// import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';
// import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
// import { GlobalInputField } from '../../components/GlobalComponents/GlobalInputField';

// interface FeesSlab {
//   fees_slab_id: string;
//   base_fee: number;
//   minimum_fee: number;
//   maximum_fee: number;
//   status: string;
//   created_at?: string;
// }

// interface Course {
//   course_id: string;
//   name: string;
//   title: string;
//   fees_slab?: FeesSlab | null;
// }

// interface Batch {
//   batch_id: string;
//   institute_id: string;
//   branch_id: string;
//   course_id: string;
//   batch_name: string;
//   batch_time: string;
//   start_month: string;
//   start_year: number;
//   end_month: string;
//   end_year: number;
//   is_active: boolean;
//   created_at: string | null;
// }

// interface AddBatchModalProps {
//   visible: boolean;
//   instituteId: string;
//   branchId: string;
//   courses: Course[];
//   editingBatch?: Batch | null;
//   onHide: () => void;
//   onSuccess?: () => void;
// }

// const MONTHS = [
//   'January', 'February', 'March', 'April', 'May', 'June',
//   'July', 'August', 'September', 'October', 'November', 'December'
// ];

// const AddBatchModal: React.FC<AddBatchModalProps> = ({
//   visible,
//   instituteId,
//   branchId,
//   courses,
//   editingBatch,
//   onHide,
//   onSuccess
// }) => {
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState<string | null>(null);
//   const [feesSlab, setFeesSlab] = useState<FeesSlab | null>(null);

//   const [formData, setFormData] = useState({
//     course_id: '',
//     batch_name: '',
//     start_time: '',
//     end_time: '',
//     start_month: '',
//     start_year: new Date().getFullYear(),
//     end_month: '',
//     end_year: new Date().getFullYear() + 1,
//     is_active: true,
//     additional_fee: ''
//   });

//   const [errors, setErrors] = useState<Record<string, string>>({});

//   // ── Get fees slab from selected course ──────────────────────────────────────
//   useEffect(() => {
//     if (!formData.course_id) {
//       setFeesSlab(null);
//       setError(null);
//       return;
//     }
//     const selectedCourse = courses.find(c => c.course_id === formData.course_id);
//     if (selectedCourse?.fees_slab) {
//       setFeesSlab(selectedCourse.fees_slab);
//       setError(null);
//     } else {
//       setFeesSlab(null);
//       setError('No fees slab found for the selected course. Please contact the administrator.');
//     }
//   }, [formData.course_id, courses]);

//   // ── Populate form when editing ───────────────────────────────────────────────
//   useEffect(() => {
//     if (editingBatch) {
//       const [startTime, endTime] = editingBatch.batch_time.split(' - ').map(t => t.trim());
//       setFormData({
//         course_id:      editingBatch.course_id,
//         batch_name:     editingBatch.batch_name,
//         start_time:     startTime || '',
//         end_time:       endTime || '',
//         start_month:    editingBatch.start_month,
//         start_year:     editingBatch.start_year,
//         end_month:      editingBatch.end_month,
//         end_year:       editingBatch.end_year,
//         is_active:      editingBatch.is_active,
//         additional_fee: ''
//       });
//     } else {
//       setFormData({
//         course_id:      '',
//         batch_name:     '',
//         start_time:     '',
//         end_time:       '',
//         start_month:    '',
//         start_year:     new Date().getFullYear(),
//         end_month:      '',
//         end_year:       new Date().getFullYear() + 1,
//         is_active:      true,
//         additional_fee: ''
//       });
//       setFeesSlab(null);
//       setError(null);
//     }
//   }, [editingBatch, visible]);

//   // ── Generate batch name ──────────────────────────────────────────────────────
//   // Format: {CourseName}-{StartMonth}-{StartYear}-{EndYear}
//   // Requires: course_id, start_month, start_year, end_year
//   const canGenerateName = !!(
//     formData.course_id &&
//     formData.start_month &&
//     formData.start_year &&
//     formData.end_year
//   );

//   const handleGenerateBatchName = () => {
//     if (!canGenerateName) return;

//     const selectedCourse = courses.find(c => c.course_id === formData.course_id);
//     if (!selectedCourse) return;

//     // Use course short name (e.g. "ADCA"), uppercased
//     const coursePart = `${selectedCourse.name} - ${selectedCourse.title}`.toUpperCase();
//     const monthPart   = formData.start_month.toUpperCase().slice(0, 3); // JAN, FEB …
//     const startPart   = formData.start_year.toString();
//     const endPart     = formData.end_year.toString();

//     const generated = `${coursePart}-${monthPart}-${startPart}-${endPart}`;

//     setFormData(prev => ({ ...prev, batch_name: generated }));

//     // Clear batch_name error if present
//     if (errors.batch_name) {
//       setErrors(prev => ({ ...prev, batch_name: '' }));
//     }
//   };

//   // ── Options ──────────────────────────────────────────────────────────────────
//   const courseOptions: DropdownOption[] = courses.map(course => ({
//     label: `${course.name} - ${course.title}`,
//     value: course.course_id
//   }));

//   const monthOptions: DropdownOption[] = MONTHS.map(month => ({
//     label: month,
//     value: month
//   }));

//   const currentYear = new Date().getFullYear();
//   const yearOptions: DropdownOption[] = Array.from(
//     { length: 7 },
//     (_, i) => currentYear - 1 + i
//   ).map(year => ({ label: year.toString(), value: year }));

//   const statusOptions: DropdownOption[] = [
//     { label: 'Active',   value: true },
//     { label: 'Inactive', value: false }
//   ];

//   // ── Input handler ────────────────────────────────────────────────────────────
//   const handleInputChange = (field: string, value: any) => {
//     setFormData(prev => ({ ...prev, [field]: value }));
//     if (errors[field]) {
//       setErrors(prev => ({ ...prev, [field]: '' }));
//     }
//     if (field === 'additional_fee' && feesSlab) {
//       const additionalFee = parseFloat(value);
//       if (value && !isNaN(additionalFee) && additionalFee > feesSlab.maximum_fee) {
//         setErrors(prev => ({
//           ...prev,
//           additional_fee: `Additional fee (₹${additionalFee.toLocaleString('en-IN')}) cannot exceed maximum allowed (₹${feesSlab.maximum_fee.toLocaleString('en-IN')})`
//         }));
//       }
//     }
//   };

//   // ── Time parsing ─────────────────────────────────────────────────────────────
//   const parseTimeToMinutes = (timeStr: string): number => {
//     if (!timeStr) return 0;
//     const [time, meridiem] = timeStr.split(' ');
//     const [hours, minutes] = time.split(':').map(Number);
//     let adjustedHours = hours;
//     if (meridiem) {
//       if (meridiem.toUpperCase() === 'PM' && hours !== 12) adjustedHours = hours + 12;
//       else if (meridiem.toUpperCase() === 'AM' && hours === 12) adjustedHours = 0;
//     }
//     return adjustedHours * 60 + minutes;
//   };

//   // ── Validation ───────────────────────────────────────────────────────────────
//   const validateForm = () => {
//     const newErrors: Record<string, string> = {};

//     if (!formData.course_id)             newErrors.course_id   = 'Course is required';
//     if (!formData.batch_name.trim())     newErrors.batch_name  = 'Batch name is required';
//     if (!formData.start_time.trim())     newErrors.start_time  = 'Start time is required';
//     if (!formData.end_time.trim())       newErrors.end_time    = 'End time is required';
//     if (!formData.start_month)           newErrors.start_month = 'Start month is required';
//     if (!formData.start_year)            newErrors.start_year  = 'Start year is required';
//     if (!formData.end_month)             newErrors.end_month   = 'End month is required';
//     if (!formData.end_year)              newErrors.end_year    = 'End year is required';

//     if (formData.start_time && formData.end_time) {
//       const startMins = parseTimeToMinutes(formData.start_time);
//       const endMins   = parseTimeToMinutes(formData.end_time);
//       if (endMins <= startMins) {
//         newErrors.end_time = 'End time must be after start time';
//       }
//     }

//     if (formData.start_year && formData.end_year && formData.start_month && formData.end_month) {
//       const startDate = new Date(formData.start_year, MONTHS.indexOf(formData.start_month));
//       const endDate   = new Date(formData.end_year,   MONTHS.indexOf(formData.end_month));
//       if (endDate <= startDate) {
//         newErrors.end_month = 'End date must be after start date';
//         newErrors.end_year  = 'End date must be after start date';
//       }
//     }

//     if (formData.additional_fee && feesSlab) {
//       const additionalFee = parseFloat(formData.additional_fee);
//       if (isNaN(additionalFee) || additionalFee < 0) {
//         newErrors.additional_fee = 'Additional fee must be a valid positive number';
//       } else if (additionalFee > feesSlab.maximum_fee) {
//         newErrors.additional_fee = `Additional fee cannot exceed ₹${feesSlab.maximum_fee.toLocaleString('en-IN')}`;
//       }
//     }

//     setErrors(newErrors);
//     return Object.keys(newErrors).length === 0;
//   };

//   // ── Submit ───────────────────────────────────────────────────────────────────
//   const handleSubmit = async () => {
//     if (!validateForm()) {
//       setError('Please fill all required fields correctly');
//       return;
//     }

//     setLoading(true);
//     setError(null);

//     try {
//       const batchTime = `${formData.start_time.trim()} - ${formData.end_time.trim()}`;
//       const payload = {
//         institute_id:   instituteId,
//         branch_id:      branchId,
//         course_id:      formData.course_id,
//         batch_name:     formData.batch_name.trim(),
//         batch_time:     batchTime,
//         start_month:    formData.start_month,
//         start_year:     formData.start_year,
//         end_month:      formData.end_month,
//         end_year:       formData.end_year,
//         is_active:      formData.is_active,
//         base_fee:       feesSlab?.base_fee || 0,
//         additional_fee: formData.additional_fee ? parseFloat(formData.additional_fee) : 0
//       };

//       let response;
//       if (editingBatch) {
//         response = await apiClient.put(`/v1/batches/${editingBatch.batch_id}`, payload);
//       } else {
//         response = await apiClient.post('/v1/batches', payload);
//       }

//       if (response.data.success) {
//         handleClose();
//         if (onSuccess) onSuccess();
//       } else {
//         setError(response.data.message || `Failed to ${editingBatch ? 'update' : 'create'} batch`);
//       }
//     } catch (err: any) {
//       setError(
//         err.response?.data?.error ||
//         err.response?.data?.message ||
//         err.message ||
//         `Failed to ${editingBatch ? 'update' : 'create'} batch`
//       );
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ── Close / reset ────────────────────────────────────────────────────────────
//   const handleClose = () => {
//     setFormData({
//       course_id: '', batch_name: '', start_time: '', end_time: '',
//       start_month: '', start_year: new Date().getFullYear(),
//       end_month: '', end_year: new Date().getFullYear() + 1,
//       is_active: true, additional_fee: ''
//     });
//     setErrors({});
//     setError(null);
//     setFeesSlab(null);
//     onHide();
//   };

//   const calculateTotalFee = () => {
//     if (!feesSlab) return 0;
//     const additionalFee = formData.additional_fee ? parseFloat(formData.additional_fee) : 0;
//     return feesSlab.base_fee + (isNaN(additionalFee) ? 0 : additionalFee);
//   };

//   // ── Render ───────────────────────────────────────────────────────────────────

//   return (
//     <Dialog
//       visible={visible}
//       onHide={handleClose}
//       style={{ width: '90vw', maxWidth: '900px' }}
//       dismissableMask
//       draggable={false}
//       pt={{
//         root: {
//           style: {
//             borderRadius: '20px',
//             overflow: 'hidden',
//             boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
//           }
//         }
//       }}
//     >
//       <div>
//         {/* Modal Header */}
//         <div style={{
//           background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
//           borderRadius: '16px', padding: '24px', marginBottom: '24px',
//           color: 'white', position: 'relative', overflow: 'hidden'
//         }}>
//           <div style={{
//             position: 'absolute', top: '-50px', right: '-50px',
//             width: '150px', height: '150px',
//             background: 'rgba(212,241,126,0.1)', borderRadius: '50%', filter: 'blur(40px)'
//           }} />
//           <div style={{ position: 'relative', zIndex: 1 }}>
//             <div style={{
//               display: 'inline-block', padding: '4px 10px',
//               background: 'rgba(212,241,126,0.2)', borderRadius: '16px',
//               marginBottom: '8px', fontSize: '0.65rem', fontWeight: '600',
//               letterSpacing: '0.5px', textTransform: 'uppercase'
//             }}>
//               Batch Management
//             </div>
//             <h2 style={{ margin: '0 0 6px 0', fontSize: '1.4rem', fontWeight: '700', color: 'white' }}>
//               {editingBatch ? 'Edit Batch' : 'Add New Batch'}
//             </h2>
//             <p style={{ margin: 0, color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem' }}>
//               {editingBatch ? 'Update batch information' : 'Create a new batch for a course'}
//             </p>
//           </div>
//         </div>

//         {error && (
//           <Message
//             severity="error"
//             text={error}
//             style={{ marginBottom: '20px', fontSize: '0.9rem', fontWeight: '500' }}
//           />
//         )}

//         {/* Form */}
//         <div style={{
//           background: 'white', borderRadius: '12px', padding: '20px',
//           marginBottom: '20px', border: '1px solid #f0f0f0'
//         }}>
//           <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

//             {/* Course */}
//             <GlobalDropdownField
//               label="Course"
//               required
//               value={formData.course_id}
//               onChange={(val) => handleInputChange('course_id', val)}
//               options={courseOptions}
//               placeholder="Select a course"
//               icon="pi pi-book"
//               error={errors.course_id}
//               disabled={!!editingBatch}
//             />

//             {/* Fees Information */}
//             {feesSlab && (
//               <div style={{
//                 background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
//                 padding: '16px', borderRadius: '12px', border: '1px solid #dee2e6'
//               }}>
//                 <h3 style={{
//                   margin: '0 0 12px 0', fontSize: '0.9rem', fontWeight: '600',
//                   color: '#0f3d3e', display: 'flex', alignItems: 'center', gap: '8px'
//                 }}>
//                   <i className="pi pi-wallet" style={{ fontSize: '0.85rem' }} />
//                   Fees Information
//                 </h3>
//                 <div style={{
//                   display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
//                   gap: '12px', marginBottom: '16px'
//                 }}>
//                   <div style={{ background: 'white', padding: '12px', borderRadius: '8px', border: '1px solid #dee2e6' }}>
//                     <p style={{ margin: '0 0 4px 0', fontSize: '0.75rem', color: '#6c757d', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
//                       Institute Proposed Fee
//                     </p>
//                     <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#0f3d3e' }}>
//                       ₹{feesSlab.base_fee.toLocaleString('en-IN')}
//                     </p>
//                   </div>
//                   <div style={{ background: 'white', padding: '12px', borderRadius: '8px', border: '1px solid #dee2e6' }}>
//                     <p style={{ margin: '0 0 4px 0', fontSize: '0.75rem', color: '#6c757d', fontWeight: '500', letterSpacing: '0.5px', lineHeight: '1.3' }}>
//                       Additional Amount Can Be Extended Upto
//                     </p>
//                     <p style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#dc3545' }}>
//                       ₹{feesSlab.maximum_fee.toLocaleString('en-IN')}
//                     </p>
//                   </div>
//                 </div>

//                 <GlobalInputField
//                   label="Additional Fee (Optional)"
//                   value={formData.additional_fee}
//                   onChange={(e) => handleInputChange('additional_fee', e.target.value)}
//                   placeholder="0.00"
//                   icon="pi pi-plus-circle"
//                   error={errors.additional_fee}
//                   type="number"
//                 />

//                 {(formData.additional_fee || formData.additional_fee === '0') && !errors.additional_fee && (
//                   <div style={{
//                     marginTop: '12px', padding: '12px',
//                     background: 'rgba(15,61,62,0.05)', borderRadius: '8px',
//                     border: '1px dashed rgba(15,61,62,0.2)',
//                     display: 'flex', justifyContent: 'space-between', alignItems: 'center'
//                   }}>
//                     <span style={{ fontSize: '0.85rem', fontWeight: '600', color: '#0f3d3e' }}>Total Batch Fee:</span>
//                     <span style={{ fontSize: '1.2rem', fontWeight: '700', color: '#0f3d3e' }}>
//                       ₹{calculateTotalFee().toLocaleString('en-IN')}
//                     </span>
//                   </div>
//                 )}

//                 <p style={{ margin: '12px 0 0 0', fontSize: '0.75rem', color: '#6c757d', fontStyle: 'italic', display: 'flex', alignItems: 'center', gap: '6px' }}>
//                   <i className="pi pi-info-circle" style={{ fontSize: '0.7rem' }} />
//                   Additional fee cannot exceed ₹{feesSlab.maximum_fee.toLocaleString('en-IN')}
//                 </p>
//               </div>
//             )}

//             {/* Start Date — needed BEFORE batch name so generate works */}
//             <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '12px', border: '1px solid #e9ecef' }}>
//               <h3 style={{ margin: '0 0 12px 0', fontSize: '0.9rem', fontWeight: '600', color: '#0f3d3e', display: 'flex', alignItems: 'center', gap: '8px' }}>
//                 <i className="pi pi-calendar-plus" style={{ fontSize: '0.85rem' }} />
//                 Start Date
//               </h3>
//               <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
//                 <GlobalDropdownField
//                   label="Start Month"
//                   required
//                   value={formData.start_month}
//                   onChange={(val) => handleInputChange('start_month', val)}
//                   options={monthOptions}
//                   placeholder="Select month"
//                   icon="pi pi-calendar"
//                   error={errors.start_month}
//                 />
//                 <GlobalDropdownField
//                   label="Start Year"
//                   required
//                   value={formData.start_year}
//                   onChange={(val) => handleInputChange('start_year', val)}
//                   options={yearOptions}
//                   placeholder="Select year"
//                   icon="pi pi-calendar"
//                   error={errors.start_year}
//                 />
//               </div>
//             </div>

//             {/* End Date */}
//             <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '12px', border: '1px solid #e9ecef' }}>
//               <h3 style={{ margin: '0 0 12px 0', fontSize: '0.9rem', fontWeight: '600', color: '#0f3d3e', display: 'flex', alignItems: 'center', gap: '8px' }}>
//                 <i className="pi pi-calendar-minus" style={{ fontSize: '0.85rem' }} />
//                 End Date
//               </h3>
//               <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
//                 <GlobalDropdownField
//                   label="End Month"
//                   required
//                   value={formData.end_month}
//                   onChange={(val) => handleInputChange('end_month', val)}
//                   options={monthOptions}
//                   placeholder="Select month"
//                   icon="pi pi-calendar"
//                   error={errors.end_month}
//                 />
//                 <GlobalDropdownField
//                   label="End Year"
//                   required
//                   value={formData.end_year}
//                   onChange={(val) => handleInputChange('end_year', val)}
//                   options={yearOptions}
//                   placeholder="Select year"
//                   icon="pi pi-calendar"
//                   error={errors.end_year}
//                 />
//               </div>
//             </div>

//             {/* Batch Name + Generate button */}
//             <div>
//               <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px' }}>
//                 {/* Input takes all remaining width */}
//                 <div style={{ flex: 1 }}>
//                   <GlobalInputField
//                     label="Batch Name"
//                     required
//                     value={formData.batch_name}
//                     onChange={(e) => handleInputChange('batch_name', e.target.value)}
//                     placeholder="e.g., ADCA-JAN-2025-2026"
//                     icon="pi pi-users"
//                     error={errors.batch_name}
//                   />
//                 </div>

//                 {/* Generate button */}
//                 <button
//                   type="button"
//                   onClick={handleGenerateBatchName}
//                   disabled={!canGenerateName}
//                   title={
//                     canGenerateName
//                       ? 'Generate batch name from selected values'
//                       : 'Select Course, Start Month, Start Year and End Year first'
//                   }
//                   style={{
//                     height: '44px',
//                     padding: '0 16px',
//                     background: canGenerateName ? '#0f3d3e' : '#e0e0e0',
//                     border: 'none',
//                     borderRadius: '10px',
//                     color: canGenerateName ? '#d4f17e' : '#aaa',
//                     fontSize: '0.8rem',
//                     fontWeight: '700',
//                     cursor: canGenerateName ? 'pointer' : 'not-allowed',
//                     display: 'flex',
//                     alignItems: 'center',
//                     gap: '6px',
//                     whiteSpace: 'nowrap',
//                     transition: 'all 0.2s ease',
//                     flexShrink: 0,
//                     // align with input field bottom (label above takes ~24px)
//                     marginBottom: errors.batch_name ? '22px' : '0'
//                   }}
//                   onMouseEnter={e => {
//                     if (canGenerateName) e.currentTarget.style.background = '#1a5555';
//                   }}
//                   onMouseLeave={e => {
//                     if (canGenerateName) e.currentTarget.style.background = '#0f3d3e';
//                   }}
//                 >
//                   <i className="pi pi-bolt" style={{ fontSize: '0.85rem' }} />
//                   Generate
//                 </button>
//               </div>

//               {/* Helper hint shown when fields are missing */}
//               {!canGenerateName && (
//                 <p style={{
//                   margin: '6px 0 0 0', fontSize: '0.72rem',
//                   color: '#999', display: 'flex', alignItems: 'center', gap: '5px'
//                 }}>
//                   <i className="pi pi-info-circle" style={{ fontSize: '0.68rem' }} />
//                   Select Course, Start Month, Start Year and End Year to auto-generate a name
//                 </p>
//               )}
//             </div>

//             {/* Batch Timings */}
//             <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '12px', border: '1px solid #e9ecef' }}>
//               <h3 style={{ margin: '0 0 12px 0', fontSize: '0.9rem', fontWeight: '600', color: '#0f3d3e', display: 'flex', alignItems: 'center', gap: '8px' }}>
//                 <i className="pi pi-clock" style={{ fontSize: '0.85rem' }} />
//                 Batch Timings
//               </h3>
//               <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
//                 <GlobalTimeField
//                   label="Start Time"
//                   required
//                   value={formData.start_time}
//                   onChange={(val) => handleInputChange('start_time', val)}
//                   placeholder="e.g., 10:00 AM"
//                   icon="pi pi-clock"
//                   error={errors.start_time}
//                   hourFormat="12"
//                 />
//                 <GlobalTimeField
//                   label="End Time"
//                   required
//                   value={formData.end_time}
//                   onChange={(val) => handleInputChange('end_time', val)}
//                   placeholder="e.g., 12:00 PM"
//                   icon="pi pi-clock"
//                   error={errors.end_time}
//                   hourFormat="12"
//                 />
//               </div>
//               {formData.start_time && formData.end_time && !errors.end_time && (
//                 <div style={{
//                   marginTop: '12px', padding: '10px',
//                   background: 'rgba(15,61,62,0.05)', borderRadius: '8px',
//                   fontSize: '0.85rem', color: '#0f3d3e', fontWeight: '600', textAlign: 'center'
//                 }}>
//                   📅 Batch Schedule: {formData.start_time} - {formData.end_time}
//                 </div>
//               )}
//             </div>

//             {/* Status */}
//             <GlobalDropdownField
//               label="Status"
//               required
//               value={formData.is_active}
//               onChange={(val) => handleInputChange('is_active', val)}
//               options={statusOptions}
//               placeholder="Select status"
//               icon="pi pi-check-circle"
//             />
//           </div>
//         </div>

//         {/* Actions */}
//         <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
//           <GlobalButton
//             label="Cancel"
//             variant="outline"
//             icon="pi pi-times"
//             onClick={handleClose}
//             disabled={loading}
//           />
//           <GlobalButton
//             label={loading
//               ? (editingBatch ? 'Updating...' : 'Creating...')
//               : (editingBatch ? 'Update Batch' : 'Create Batch')}
//             variant="primary"
//             icon={loading ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
//             onClick={handleSubmit}
//             disabled={loading || !feesSlab}
//             loading={loading}
//           />
//         </div>
//       </div>
//     </Dialog>
//   );
// };

// export default AddBatchModal;